<template>
  <a-modal
    :visible="value"
    :title="data ? 'Edit filter' : 'Add filter'"
    width="445px"
    destroyOnClose
    @cancel="$emit('close')"
  >
    <div style="position: relative">
      <LanguageSelect
        v-model="selectedLanguage"
        :langs="langs"
        :contentFilled="filledLangs"
        style="
          position: static !important;
          display: flex;
          justify-content: flex-end;
        "
        :error="formErrorState"
        short-button-label
        @copyFromDefault="handleCopyFromDefault"
        @clear="handleClearLang"
      />
      <a-form-model :model="currentForm" :rules="rules" ref="form">
        <a-form-model-item :colon="false" label="Title" prop="title">
          <a-input v-model="currentForm.title" size="large" />
        </a-form-model-item>
        <a-form-model-item :colon="false" prop="matches">
          <template #label>
            <span>Match</span>
            <a-tooltip
              slot="suffix"
              title="In this filter you can select only the matches that you selected earlier in the 'Content' section"
            >
              <a-icon type="info-circle" class="text-primary" />
            </a-tooltip>
          </template>
          <a-select
            v-model="currentForm.matches"
            mode="multiple"
            size="large"
            placeholder="Choose matches"
            @change="handleMatchesChange"
          >
            <a-select-option v-for="match in currentSelectedMatches" :key="match.id" :value="match.id">
              {{ match.name }} {{ $moment(match.date).format('DD.MM.YYYY') }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
      </a-form-model>
    </div>
    <template slot="footer">
      <a-button key="cancel" @click="$emit('close')"> Cancel </a-button>
      <a-button key="save" type="primary" @click="handleSubmit">
        Save
      </a-button>
    </template>
  </a-modal>
</template>
<script>
import LanguageSelect from '@/components/custom/languageSelect.vue'

import { DEFAULT_LANG } from '@/constants/lang'

import { mapState, mapGetters } from 'vuex'

import isEqual from 'lodash/isEqual'

const FORM_FIELDS = {
  title: '',
  matches: [],
}

export default {
  components: {
    LanguageSelect,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
    },
    initLang: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      form: {
        ru: { ...FORM_FIELDS },
      },
      rules: {
        title: [{ required: true, message: 'Enter a title', trigger: 'blur' }],
        matches: [
          {
            required: true,
            message: 'Select at least one match',
            trigger: ['blur', 'change'],
          },
        ],
      },
      selectedLanguage: 'ru',
      validationResult: true,
    }
  },
  computed: {
    ...mapState({
      langs: (state) => state.langs.langData,
    }),
    ...mapGetters('content', ['selectedMatches']),
    currentForm() {
      return this.form[this.selectedLanguage]
    },
    currentSelectedMatches() {
      const matchIds = new Set()
      const uniqueMatches = this.selectedMatches[this.selectedLanguage].filter(m => {
        const duplicate = matchIds.has(m.id)
        matchIds.add(m.id)

        return !duplicate
      })

      return uniqueMatches
    },
    editId() {
      return Object.values(this.form).filter(lang => lang.id)?.[0]?.id
    },
    isInitialForm() {
      return JSON.stringify(this.currentForm) === JSON.stringify(FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      return this.langs.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.form[name], FORM_FIELDS)
        return obj
      }, {})
    },
  },
  methods: {
    createFields() {
      return { ...JSON.parse(JSON.stringify(FORM_FIELDS)) }
    },
    initForm() {
      if (this.langs.length === 0) {
        return {
          ru: this.createFields(),
        }
      }

      this.form = this.langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: this.data?.[lang.name] ? this.data?.[lang.name] : this.createFields(),
        }
      }, {})
    },
    handleMatchesChange() {
      this.$refs.form.validateField('matches')
    },
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          const filteredForm = Object.fromEntries(
            Object.entries(this.form).filter(
              ([key, val]) =>
                JSON.stringify(val) !== JSON.stringify(FORM_FIELDS),
            ),
          )

          const matchMap = new Map(
            this.currentSelectedMatches.map(match => [match.id, match]),
          )

          const resultForm = Object.fromEntries(
            Object.entries(filteredForm).map(([lang, val]) => {
              const matches = (val.matches || []).map(matchId => matchMap.get(matchId)).filter(Boolean)

              return [lang, { ...val, matches }]
            }),
          )

          this.$emit('submit', resultForm, this.editId ?? null)
        }
      })
    },
    handleCopyFromDefault() {
      const defaultForm = this.form[DEFAULT_LANG]

      const result = {
        ...defaultForm,
        matches: defaultForm?.matches?.filter(m => this.currentSelectedMatches?.includes(m)),
      }

      this.form[this.selectedLanguage] = JSON.parse(JSON.stringify(result))
    },
    validateForm() {
      if (!this.$refs.form) return
      this.$refs.form.validate((valid) => {
        this.validationResult = valid
      })
    },
    handleClearLang(lang) {
      this.form[lang] = { ...FORM_FIELDS }

      this.$refs.form.clearValidate()
    },
  },
  created() {
    this.initForm()

    this.selectedLanguage = this.initLang
  },
  watch: {
    currentForm: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
}
</script>
<style scoped lang="scss">
::v-deep label {
  margin: 0;
}

::v-deep .ant-form-item-label {
  overflow: visible;
}

::v-deep .ant-tooltip-inner {
  font-weight: 400;
  white-space: initial;
}

::v-deep .ant-form-item label {
  position: static;

  .anticon {
    margin-left: 4px;
  }
}

::v-deep .ant-form-item-label {
  position: static;
}
</style>
